import { EditOutlined, EyeOutlined } from "@ant-design/icons"
import { Button, Flex, Tag, Typography } from "antd"
import React from "react"

const { Text } = Typography

interface DocumentVersionRowProps {
  /** `version_number` from the real Proposal/Contract detail response. */
  versionNumber: number
  /** `is_latest_version` — real backend field, confirmed live on both the
   * Proposal and Contract detail endpoints. NOT `is_editable`: that folds in
   * status-based PATCH guards too, so a latest version can still be
   * `is_editable: false` for other reasons — this label is about version
   * standing specifically, matching the product spec's "Latest"/"Read-only"
   * wording. */
  isLatest: boolean
  /** Omit both to render as a plain badge (e.g. inline next to a row that
   * already has its own Edit/View controls elsewhere). */
  onEdit?: () => void
  onView?: () => void
}

/**
 * Per the product owner's version-immutability spec: "Version 3 — Latest" /
 * "Version 2 — Read-only", `[Edit]` offered only on the latest version,
 * `[View]` on every earlier one.
 *
 * NOT the same "version" as `is_version_2` — that flag distinguishes the
 * block-based (V2) document editor from the legacy (V1) one, an unrelated
 * axis from "is this Proposal/Contract record the latest in its own
 * version lineage".
 *
 * Known gap (see `docs/kb` / the version-immutability report): the backend's
 * `version_history` array (on `Proposal`/`Contract`/`Document`) lists every
 * record sharing a lineage, but each entry carries only `is_current` (which
 * marks "the record whose own detail endpoint you called", not "the latest
 * one") — no per-entry `is_latest_version`. Confirmed live: several entries
 * in one `version_history` array can each independently report
 * `is_latest_version: true` on their own detail fetch, so a full multi-row
 * "Version 3 — Latest / Version 2 — Read-only / Version 1 — Read-only" list
 * cannot be rendered correctly from `version_history` alone without either
 * N extra per-entry fetches or a backend addition of `is_latest_version` to
 * each entry. Until then this renders one row for a single, already-fetched
 * document (its own confirmed `version_number`/`is_latest_version`), not a
 * full lineage list.
 */
export function DocumentVersionRow({
  versionNumber,
  isLatest,
  onEdit,
  onView
}: DocumentVersionRowProps) {
  const showActions = !!onEdit || !!onView

  return (
    <Flex
      align="center"
      justify={showActions ? "space-between" : undefined}
      className={showActions ? "w-full" : undefined}
      gap={showActions ? undefined : "small"}
    >
      <Flex align="center" gap="small">
        <Text strong>Version {versionNumber}</Text>
        <Tag color={isLatest ? "blue" : "default"}>
          {isLatest ? "Latest" : "Read-only"}
        </Tag>
      </Flex>
      {showActions &&
        (isLatest ? (
          <Button size="small" icon={<EditOutlined />} onClick={onEdit}>
            Edit
          </Button>
        ) : (
          <Button size="small" icon={<EyeOutlined />} onClick={onView}>
            View
          </Button>
        ))}
    </Flex>
  )
}
